"use client";

import { useEffect, useId, useRef, useState } from "react";

import {
  CV_ACCEPT,
  CV_CONTENT_TYPES,
  CV_PROBLEM_MESSAGES,
  cvFileProblem,
  cvKindFromBytes,
  cvKindFromName,
  cvRequestPath,
  formatFileSize,
} from "@/lib/careers-cv";

/**
 * The CV upload on the careers form.
 *
 * The file goes from the browser straight to Lead Engine's PRIVATE store. /api/careers/cv-upload
 * only hands out permission (one Cloudflare check per upload, rate limited), then `put` sends the
 * bytes with a token that only allows one CV path, 5 MB, PDF / DOC / DOCX. Lead Engine checks the
 * file's real contents again when the application is sent, and refuses (and deletes) anything else.
 *
 * Every state is drawn: empty, waiting for the security check, uploading (with progress and a
 * cancel), done (name, size, replace, remove), and each failure in plain words with a way on.
 */

export type UploadedCv = { pathname: string; fileName: string; sizeBytes: number };

type State =
  | { kind: "empty" }
  | { kind: "queued"; file: File }
  | { kind: "uploading"; file: File; percent: number }
  | { kind: "done"; file: File; cv: UploadedCv }
  /** `retryable` only for a failed send (network, storage): retrying a refused file cannot help. */
  | { kind: "error"; message: string; file?: File; retryable?: boolean };

export type CvUploadFieldProps = {
  /** Current Cloudflare token, or null while the check is still running. */
  turnstileToken: string | null;
  /** Whether a request can go now (no check configured, or it has passed). */
  turnstileReady: boolean;
  /** Called after an upload uses the token, so the form asks Cloudflare for a fresh one. */
  onTurnstileUsed: () => void;
  onChange: (cv: UploadedCv | null) => void;
  /** Lets the form report "still uploading" instead of sending without the file. */
  onBusyChange: (busy: boolean) => void;
  disabled?: boolean;
  /** Set by the form when Lead Engine refused the file, so the field shows why. */
  externalError?: string | null;
};

const TEST_PATH = (kind: string) => `applicant-cvs/uploads/cv-PREVIEWONLY000.${kind}`;

async function readHead(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, 8).arrayBuffer());
}

export function CvUploadField({
  turnstileToken,
  turnstileReady,
  onTurnstileUsed,
  onChange,
  onBusyChange,
  disabled,
  externalError,
}: CvUploadFieldProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [state, setState] = useState<State>({ kind: "empty" });
  const [dragging, setDragging] = useState(false);

  const busy = state.kind === "uploading" || state.kind === "queued";
  useEffect(() => onBusyChange(busy), [busy, onBusyChange]);

  // A refusal from the server (the file failed the content check) becomes this field's error.
  useEffect(() => {
    if (!externalError) return;
    const t = setTimeout(() => {
      setState({ kind: "error", message: externalError });
      onChange(null);
    }, 0);
    return () => clearTimeout(t);
  }, [externalError, onChange]);

  async function start(file: File) {
    const kind = cvKindFromName(file.name);
    if (!kind) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ kind: "uploading", file, percent: 0 });
    try {
      let res: Response;
      try {
        res = await fetch("/api/careers/cv-upload", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-turnstile-token": turnstileToken ?? "" },
          body: JSON.stringify({
            type: "blob.generate-client-token",
            payload: { pathname: cvRequestPath(kind), clientPayload: null, multipart: false },
          }),
          signal: controller.signal,
        });
      } finally {
        // The check is single use whatever happened, so ask for a fresh one for the form.
        onTurnstileUsed();
      }
      const data = (await res.json().catch(() => ({}))) as { clientToken?: string; test?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error || CV_PROBLEM_MESSAGES.failed);

      let pathname: string;
      if (data.test) {
        // Preview or local rehearsal: nothing is stored anywhere. Walk the bar so the state shows.
        for (const p of [25, 60, 100]) {
          await new Promise((r) => setTimeout(r, 180));
          if (controller.signal.aborted) throw new DOMException("aborted", "AbortError");
          setState({ kind: "uploading", file, percent: p });
        }
        pathname = TEST_PATH(kind);
      } else {
        if (!data.clientToken) throw new Error(CV_PROBLEM_MESSAGES.failed);
        // Loaded on demand so the upload library only reaches people who attach a file.
        const { put } = await import("@vercel/blob/client");
        const blob = await put(cvRequestPath(kind), file, {
          access: "private",
          token: data.clientToken,
          contentType: CV_CONTENT_TYPES[kind],
          abortSignal: controller.signal,
          onUploadProgress: ({ percentage }) =>
            setState({ kind: "uploading", file, percent: Math.round(percentage) }),
        });
        pathname = blob.pathname;
      }
      const cv = { pathname, fileName: file.name, sizeBytes: file.size };
      setState({ kind: "done", file, cv });
      onChange(cv);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setState({ kind: "empty" });
        onChange(null);
        return;
      }
      // Our own route's messages are already written for a person; anything else (a network
      // drop mid-upload, the storage service refusing) gets the plain fallback.
      const message =
        err instanceof Error && /[.!]$/.test(err.message) && !/fetch|network|status|token/i.test(err.message)
          ? err.message
          : CV_PROBLEM_MESSAGES.failed;
      setState({ kind: "error", message, file, retryable: true });
      onChange(null);
    } finally {
      abortRef.current = null;
    }
  }

  // A file picked before the Cloudflare check finished waits here, then goes by itself.
  useEffect(() => {
    if (state.kind !== "queued" || !turnstileReady) return;
    const file = state.file;
    const t = setTimeout(() => void start(file), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, turnstileReady]);

  async function choose(file: File | undefined) {
    if (!file || disabled) return;
    abortRef.current?.abort();
    onChange(null);
    const problem = cvFileProblem(file);
    if (problem) {
      setState({ kind: "error", message: CV_PROBLEM_MESSAGES[problem], file });
      return;
    }
    // Instant check of the first bytes, so a renamed file is caught before it uploads.
    const head = await readHead(file).catch(() => null);
    if (!head || cvKindFromBytes(head) !== cvKindFromName(file.name)) {
      setState({ kind: "error", message: CV_PROBLEM_MESSAGES.mismatch, file });
      return;
    }
    if (turnstileReady) void start(file);
    else setState({ kind: "queued", file });
  }

  function remove() {
    abortRef.current?.abort();
    setState({ kind: "empty" });
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.focus();
  }

  const statusId = `${id}-status`;
  const hintId = `${id}-hint`;
  const isError = state.kind === "error";
  const hasFile = state.kind === "done";

  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium uppercase tracking-[0.12em] text-white/55" id={`${id}-label`}>
        Your CV
      </span>
      <div
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void choose(e.dataTransfer.files?.[0]);
        }}
        className={`ownly-cv-drop relative rounded-2xl border px-4 py-4 transition-[border-color,background-color,box-shadow] has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ownly-accent/70 sm:px-5 ${
          isError
            ? "border-red-400/45 bg-red-400/[0.05]"
            : hasFile
              ? "border-ownly-accent/55 bg-ownly-accent/[0.07]"
              : dragging
                ? "border-ownly-accent/70 bg-ownly-accent/[0.09]"
                : "border-dashed border-white/[0.2] bg-ownly-surface/70"
        }`}
      >
        <input
          ref={inputRef}
          id={`${id}-file`}
          type="file"
          accept={CV_ACCEPT}
          className="sr-only"
          disabled={disabled || busy}
          aria-labelledby={`${id}-label ${id}-pick`}
          aria-describedby={`${hintId} ${statusId}`}
          aria-invalid={isError || undefined}
          onChange={(e) => void choose(e.target.files?.[0] ?? undefined)}
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <span
            aria-hidden
            className={`grid size-11 shrink-0 place-items-center rounded-xl border ${
              hasFile
                ? "border-ownly-accent/50 bg-ownly-accent/15 text-ownly-accent-text"
                : isError
                  ? "border-red-400/40 bg-red-400/10 text-red-200"
                  : "border-white/15 bg-white/[0.04] text-white/75"
            }`}
          >
            {hasFile ? (
              <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" />
                <path d="M14 3v5h5M12 17v-6M9.5 13.5L12 11l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>

          <div className="min-w-0 flex-1" id={statusId} aria-live="polite">
            {state.kind === "empty" ? (
              <>
                <p className="text-copy font-semibold text-white">Upload your CV</p>
                <p id={hintId} className="mt-0.5 text-sm text-white/68">
                  PDF or Word, up to 5 MB. Drop it here or choose a file.
                </p>
              </>
            ) : state.kind === "queued" ? (
              <>
                <p className="truncate text-copy font-semibold text-white">{state.file.name}</p>
                <p id={hintId} className="mt-0.5 text-sm text-white/68">
                  Waiting for the security check, then it uploads by itself.
                </p>
              </>
            ) : state.kind === "uploading" ? (
              <>
                <p className="truncate text-copy font-semibold text-white">{state.file.name}</p>
                <div
                  role="progressbar"
                  aria-label="Uploading your CV"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={state.percent}
                  className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.1]"
                >
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-ownly-accent to-[#7c5cff] transition-[width] duration-200"
                    style={{ width: `${Math.max(4, state.percent)}%` }}
                  />
                </div>
                <p id={hintId} className="mt-1.5 text-sm text-white/68">
                  Uploading, {state.percent}%
                </p>
              </>
            ) : state.kind === "done" ? (
              <>
                <p className="truncate text-copy font-semibold text-white">{state.cv.fileName}</p>
                <p id={hintId} className="mt-0.5 text-sm text-white/72">
                  Uploaded, {formatFileSize(state.cv.sizeBytes)}. Only our hiring team can open it.
                </p>
              </>
            ) : (
              <>
                {state.file ? <p className="truncate text-copy font-semibold text-white">{state.file.name}</p> : null}
                <p id={hintId} className="mt-0.5 text-sm text-red-100/95">
                  {state.message}
                </p>
              </>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            {state.kind === "uploading" || state.kind === "queued" ? (
              <button type="button" onClick={remove} className="ownly-cv-btn">
                Cancel
              </button>
            ) : (
              <>
                <label
                  htmlFor={`${id}-file`}
                  id={`${id}-pick`}
                  className={`ownly-cv-btn ${
                    disabled ? "pointer-events-none opacity-50" : "cursor-pointer"
                  }`}
                >
                  {state.kind === "done" ? "Replace" : state.kind === "error" ? "Choose another" : "Choose file"}
                </label>
                {state.kind === "error" && state.file && state.retryable ? (
                  <button type="button" onClick={() => state.file && void choose(state.file)} className="ownly-cv-btn">
                    Try again
                  </button>
                ) : null}
                {state.kind === "done" || state.kind === "error" ? (
                  <button type="button" onClick={remove} className="ownly-cv-btn ownly-cv-btn-quiet">
                    Remove
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
